import {
    type LocationInputComponentProps,
    LocationInputComponentCore,
} from './LocationInputComponentCore';

import { breakpoints } from '@indeed/ifl-tokens';
import React, { useCallback, useState, useLayoutEffect } from 'react';

// Guarded against SSR: the rspack server config falls back from `.server.*` to
// `.web.*` extensions, so this module is evaluated on the JSF SSR server too.
// Resolve lazily and treat the "no window" case as non-desktop (mobile-web is the fallback layout).
const mediaQuery: MediaQueryList | null =
    typeof window !== 'undefined'
        ? window.matchMedia(`screen and (min-width: ${breakpoints.sm})`)
        : null;

const initialMediaQueryMatch: boolean = mediaQuery?.matches ?? false;

/**
 * Hook to detect if we're on desktop (based on screen width)
 * Uses the same breakpoint as AutoCompleteElement.web.tsx
 */
function useIsDesktop(): boolean {
    const [isDesktop, setIsDesktop] = useState(initialMediaQueryMatch);
    const handleMediaQueryChange = useCallback((event: MediaQueryListEvent) => {
        setIsDesktop(event.matches);
    }, []);

    useLayoutEffect(() => {
        if (!mediaQuery) return;
        mediaQuery.addEventListener('change', handleMediaQueryChange);
        return () => {
            mediaQuery.removeEventListener('change', handleMediaQueryChange);
        };
    }, [handleMediaQueryChange]);

    return isDesktop;
}

/**
 * LocationInputElement for web platform
 * Uses inline autocomplete on desktop, modal on mobile
 */
export const LocationInputElement: React.FC<LocationInputComponentProps> = (props) => {
    const isDesktop = useIsDesktop();

    // Desktop: Use inline autocomplete
    if (isDesktop) {
        return <LocationInputComponentCore {...props} useModal={false} />;
    }

    // Mobile web: Use modal with bottom sheet
    return <LocationInputComponentCore {...props} useModal={true} />;
};
